import { VISION_CAP_TILES, WORLD_TILE_PIXELS, defaultWorldZoom, stepWorldZoom, uiFit, uiScale, type Size } from "./vision.ts";

/**
 * Render resolution (ADR-093 decision 10): the canvas's backing store is the window's device size divided by a
 * whole divisor, Full (1), Half (2) or Third (3), and stretched back by exactly that many device pixels a render
 * pixel. The UI scale and the world zoom are computed from the render size, and above Full the zoom is a whole
 * number, so every art pixel is a whole block of device pixels and the sharp pass for 2.5 never runs. A divisor
 * is offered only while its render size keeps the floor. Visual only: no offered divisor shows more of the world
 * than Full does, at the default World zoom or at either step from it, and none passes the vision cap at the
 * default, which the enumerations in `render.test.ts` hold.
 */

/**
 * A render divisor: device pixels a render pixel on each axis.
 */
export type RenderDivisor = 1 | 2 | 3;

/**
 * Every divisor, Full first.
 */
export const RENDER_DIVISORS: readonly RenderDivisor[] = Object.freeze([1, 2, 3] as const);

/**
 * The smallest render size a divisor above Full may leave, in render pixels: Half is offered from 2560 by 1280
 * device pixels and Third from 3840 by 1920. The knob of the offer.
 */
export const RENDER_FLOOR: Size = Object.freeze({ width: 1280, height: 640 });

/**
 * The canvas's backing size at a divisor.
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @returns Each side divided by the divisor and rounded up, so the stretched canvas covers the window and passes
 *   it by less than the divisor in device pixels.
 */
export function renderSize(device: Size, divisor: RenderDivisor): Size {
  return { width: Math.ceil(device.width / divisor), height: Math.ceil(device.height / divisor) };
}

/**
 * The divisors a window offers: Full always, and each larger one while its render size is at least `RENDER_FLOOR`
 * on both sides.
 *
 * @param device The window's size in device pixels.
 * @returns The offered divisors in rising order, Full first.
 * @example
 * offeredDivisors({ width: 2560, height: 1440 });
 * // [1, 2]
 */
export function offeredDivisors(device: Size): RenderDivisor[] {
  return RENDER_DIVISORS.filter((divisor) => {
    if (divisor === 1) {
      return true;
    }
    const render = renderSize(device, divisor);
    return render.width >= RENDER_FLOOR.width && render.height >= RENDER_FLOOR.height;
  });
}

/**
 * The divisor in force for a stored choice: the stored one while the window offers it, else the largest offered
 * below it, so a window made smaller falls back without the stored choice being rewritten.
 *
 * @param stored The viewer's stored divisor.
 * @param device The window's size in device pixels.
 * @returns The divisor to draw at; Full when nothing above it is offered.
 */
export function resolveDivisor(stored: RenderDivisor, device: Size): RenderDivisor {
  let resolved: RenderDivisor = 1;
  for (const divisor of offeredDivisors(device)) {
    if (divisor <= stored) {
      resolved = divisor;
    }
  }
  return resolved;
}

/**
 * The default world zoom at a divisor, in render pixels a world pixel. Full is the window's default, 2.5 where it
 * fits. Above Full it is the smallest whole number at or above the exact fit of the vision cap to the render
 * size, and at least 1: the divisor times a whole zoom at or above the render fit is a whole number at or above
 * the device fit, and Full's zoom is the smallest allowed one there, so the view never grows.
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @returns The zoom: 3 at 2560 by 1440 and 2 at Half there; 5 at 3840 by 2160, 3 at Half and 2 at Third.
 */
export function renderWorldZoom(device: Size, divisor: RenderDivisor): number {
  if (divisor === 1) {
    return defaultWorldZoom(device.width, device.height);
  }
  const render = renderSize(device, divisor);
  const fit = Math.max(render.width / (WORLD_TILE_PIXELS * VISION_CAP_TILES.width), render.height / (WORLD_TILE_PIXELS * VISION_CAP_TILES.height));
  return Math.max(1, Math.ceil(fit));
}

/**
 * The world zoom at a divisor and a step of the viewer's World zoom, in render pixels a world pixel. At Full it is
 * the window's default moved by the step (`stepWorldZoom`). Above Full the render default is moved the same way
 * and then held at or above Full's zoom at the same step over the divisor, rounded up, so the divisor times the
 * zoom is never below Full's zoom there and no step shows more of the world than Full does at that step. Where
 * the hold binds, a step gives the same zoom as the one before it: one step farther at Half on a 2560 by 1496
 * window stays at the default 2, since 1 would draw a tile at 2 device pixels a world pixel where Full draws 3.
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @param step The viewer's step from the default: -1, 0 or 1.
 * @returns The zoom, a whole number above Full.
 */
export function steppedWorldZoom(device: Size, divisor: RenderDivisor, step: number): number {
  const full = stepWorldZoom(defaultWorldZoom(device.width, device.height), step);
  return divisor === 1 ? full : Math.max(stepWorldZoom(renderWorldZoom(device, divisor), step), Math.ceil(full / divisor));
}

/**
 * Whether a step of the World zoom changes anything at a divisor: false where the hold of `steppedWorldZoom`
 * leaves it at the default's zoom.
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @param step The step.
 * @returns True for the default step and for a step that gives another zoom than the default.
 */
export function zoomStepMoves(device: Size, divisor: RenderDivisor, step: number): boolean {
  return step === 0 || steppedWorldZoom(device, divisor, step) !== steppedWorldZoom(device, divisor, 0);
}

/**
 * The UI scale at a divisor: the window's own scale at Full (`uiScale`, with the small window's lift), and above
 * Full the fit alone of the render size (`uiFit`), since a render size is no small window: the lift there would
 * draw the HUD larger at Half or Third than at Full.
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @returns The scale: 3 at 2560 by 1380 at Full and 1 at Half there, whose render size is 1280 by 690.
 */
export function renderUiScale(device: Size, divisor: RenderDivisor): number {
  if (divisor === 1) {
    return uiScale(device.width, device.height);
  }
  const render = renderSize(device, divisor);
  return uiFit(render.width, render.height);
}

/**
 * The HUD's scale at a divisor in device pixels a logical pixel: the UI scale of the render size times the
 * divisor, which is what the DOM and the canvas both draw one logical pixel as.
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @returns The scale: 4 at 2560 by 1440 at Full and at Half; 3 at Full and 2 at Half at 2560 by 1380.
 */
export function hudScale(device: Size, divisor: RenderDivisor): number {
  return renderUiScale(device, divisor) * divisor;
}

/**
 * The window at a divisor in tiles, whatever the vision cap: the window's device size over the device pixels a
 * tile is drawn at (`steppedWorldZoom`). At the default step this is inside the cap at every offered divisor; one
 * step farther it can pass it, and the part past the cap is fog. At no step is it wider or taller than Full's.
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @param step The viewer's world zoom step from the default: -1, 0 or 1.
 * @returns The window in tiles, fractions kept.
 * @example
 * viewTiles({ width: 2560, height: 1440 }, 2);
 * // { width: 20, height: 11.25 }
 */
export function viewTiles(device: Size, divisor: RenderDivisor, step: number = 0): Size {
  const tile = WORLD_TILE_PIXELS * steppedWorldZoom(device, divisor, step) * divisor;
  return { width: device.width / tile, height: device.height / tile };
}

/**
 * How much of the world a window shows at a divisor, in tiles: the window in tiles and never more than the vision
 * cap, past which only fog shows (ADR-061 decision 6).
 *
 * @param device The window's size in device pixels.
 * @param divisor The divisor.
 * @param step The viewer's world zoom step from the default: -1, 0 or 1.
 * @returns The world shown in tiles, fractions kept.
 */
export function shownTiles(device: Size, divisor: RenderDivisor, step: number = 0): Size {
  const view = viewTiles(device, divisor, step);
  return { width: Math.min(VISION_CAP_TILES.width, view.width), height: Math.min(VISION_CAP_TILES.height, view.height) };
}

/**
 * What a window draws at a divisor: the render size, the render pixels a CSS pixel, the default world zoom, the
 * UI scale, and the DOM's logical pixel in CSS pixels.
 */
export interface RenderLayout {
  readonly size: Size;
  readonly ratio: number;
  readonly zoom: number;
  readonly uiScale: number;
  readonly px: number;
}

/**
 * The layout of a window at a divisor: everything the page computes from the window, the pixel ratio and the
 * divisor.
 *
 * @param device The window's size in device pixels.
 * @param ratio Device pixels a CSS pixel (`devicePixelRatio`).
 * @param divisor The divisor.
 * @returns The render size; the ratio over the divisor; the default world zoom; the UI scale of the render size,
 *   which is `--ui-scale`; and `--px`, the UI scale times the divisor over the ratio.
 */
export function renderLayout(device: Size, ratio: number, divisor: RenderDivisor): RenderLayout {
  const size = renderSize(device, divisor);
  const scale = renderUiScale(device, divisor);
  return { size, ratio: ratio / divisor, zoom: renderWorldZoom(device, divisor), uiScale: scale, px: (scale * divisor) / ratio };
}

/**
 * What a change of divisor costs the viewer: the view before and after when less of the world would show on
 * either axis, and the HUD's scale before and after when it would be smaller. Null for a part that loses nothing.
 */
export interface RenderChange {
  readonly world: { readonly from: Size; readonly to: Size } | null;
  readonly hud: { readonly from: number; readonly to: number } | null;
}

/**
 * What moving from one divisor to another costs on a window.
 *
 * @param device The window's size in device pixels.
 * @param from The divisor in force.
 * @param to The divisor chosen.
 * @param step The viewer's world zoom step, which both views are measured at.
 * @returns The losses, each null when that part is the same or better.
 */
export function renderChange(device: Size, from: RenderDivisor, to: RenderDivisor, step: number = 0): RenderChange {
  const before = shownTiles(device, from, step);
  const after = shownTiles(device, to, step);
  const hudBefore = hudScale(device, from);
  const hudAfter = hudScale(device, to);
  return {
    world: after.width < before.width || after.height < before.height ? { from: before, to: after } : null,
    hud: hudAfter < hudBefore ? { from: hudBefore, to: hudAfter } : null,
  };
}
